import React, { useState } from 'react';
import {
  X,
  AlertTriangle,
  User,
  Calendar,
  MessageSquare,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Image,
  Video,
  Heart,
  Share2
} from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../api';
import { toast } from 'react-toastify';

const SignalementDetail = ({ signalement, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [updating, setUpdating] = useState(false);
  const [commentaire, setCommentaire] = useState(signalement.commentaire_traitement || '');

  if (!signalement) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (statut) => {
    const colors = {
      'EN_ATTENTE': 'bg-orange-100 text-orange-700',
      'EN_COURS': 'bg-blue-100 text-blue-700',
      'RESOLU': 'bg-green-100 text-green-700',
      'REJETE': 'bg-red-100 text-red-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (statut) => {
    const labels = {
      'EN_ATTENTE': 'En attente',
      'EN_COURS': 'En cours',
      'RESOLU': 'Résolu',
      'REJETE': 'Rejeté'
    };
    return labels[statut] || statut;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'CONTENU_INAPPROPRIE': 'Contenu inapproprié',
      'SPAM': 'Spam',
      'HARCELEMENT': 'Harcèlement',
      'FAUSSE_INFORMATION': 'Fausse information',
      'AUTRE': 'Autre'
    };
    return labels[type] || type;
  };

  const handleUpdateStatus = async (newStatus) => {
    const result = await Swal.fire({
      title: 'Changer le statut ?',
      text: `Le signalement sera marqué comme "${getStatusLabel(newStatus)}"`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, changer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      try {
        setUpdating(true);
        const response = await api.patch(`/api/publication/admin/signalements/${signalement.id}/`, {
          statut: newStatus,
          commentaire_traitement: commentaire
        });

        Swal.fire({
          title: 'Statut mis à jour !',
          text: 'Le signalement a été mis à jour avec succès.',
          icon: 'success',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          timer: 2000,
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });

        if (onUpdate) {
          onUpdate(response.data);
        }
        onClose();
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error);
        Swal.fire({
          title: 'Erreur !',
          text: 'Erreur lors de la mise à jour du statut.',
          icon: 'error',
          confirmButtonColor: '#FF6B00',
          confirmButtonText: 'OK',
          customClass: {
            popup: 'rounded-xl',
            confirmButton: 'rounded-lg px-6 py-2'
          }
        });
      } finally {
        setUpdating(false);
      }
    }
  };

  const handleViewPublication = async () => {
    try {
      const response = await api.get(`/api/publication/admin/publications/${signalement.publication}/`);
      // Ouvrir la modal de la publication
      toast.info('Fonctionnalité à implémenter: voir la publication complète');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement de la publication');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[#FF6B00] to-[#437011]">
              <div>
                <h2 className="text-2xl font-bold text-white">Signalement #{signalement.id}</h2>
                <p className="text-white text-sm opacity-90">{getTypeLabel(signalement.type_signalement)}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveTab('info')}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'info'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Informations
              </button>
              <button
                onClick={() => setActiveTab('publication')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'publication'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MessageSquare size={18} />
                Publication
              </button>
              <button
                onClick={() => setActiveTab('traitement')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'traitement'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText size={18} />
                Traitement
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Statut */}
                  <div className={`rounded-lg p-4 ${getStatusColor(signalement.statut)}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={18} />
                      <h3 className="font-semibold">Statut actuel</h3>
                    </div>
                    <p className="ml-7 font-medium">{getStatusLabel(signalement.statut)}</p>
                  </div>

                  {/* Utilisateur signalant */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={18} className="text-[#FF6B00]" />
                      <h3 className="font-semibold text-gray-900">Signalé par</h3>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {signalement.utilisateur?.first_name?.[0] || signalement.utilisateur?.username?.[0] || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {signalement.utilisateur?.first_name && signalement.utilisateur?.last_name
                            ? `${signalement.utilisateur.first_name} ${signalement.utilisateur.last_name}`
                            : signalement.utilisateur?.username || 'Utilisateur'}
                        </p>
                        <p className="text-sm text-gray-600">{signalement.utilisateur?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-blue-600" />
                        <h3 className="font-semibold text-blue-900">Créé le</h3>
                      </div>
                      <p className="text-blue-700 ml-7">{formatDate(signalement.created_at)}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-purple-600" />
                        <h3 className="font-semibold text-purple-900">Mis à jour le</h3>
                      </div>
                      <p className="text-purple-700 ml-7">{formatDate(signalement.updated_at)}</p>
                    </div>
                  </div>

                  {/* Traité par */}
                  {signalement.traite_par && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <User size={18} className="text-green-600" />
                        <h3 className="font-semibold text-green-900">Traité par</h3>
                      </div>
                      <div className="flex items-center gap-3 ml-7">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold">
                          {signalement.traite_par.first_name?.[0] || 'A'}
                        </div>
                        <div>
                          <p className="font-medium text-green-900">
                            {signalement.traite_par.first_name} {signalement.traite_par.last_name}
                          </p>
                          <p className="text-sm text-green-700">{signalement.traite_par.email}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'publication' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Aperçu de la publication</h3>
                    {signalement.publication_preview ? (
                      <p className="text-gray-700 whitespace-pre-wrap p-4 bg-white rounded-lg">
                        {signalement.publication_preview}
                      </p>
                    ) : (
                      <p className="text-gray-400 italic">Aucun aperçu disponible</p>
                    )}
                  </div>

                  <button
                    onClick={handleViewPublication}
                    className="w-full px-6 py-3 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium"
                  >
                    <MessageSquare size={18} />
                    Voir la publication complète
                  </button>
                </div>
              )}

              {activeTab === 'traitement' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Commentaire de traitement</h3>
                    <textarea
                      value={commentaire}
                      onChange={(e) => setCommentaire(e.target.value)}
                      placeholder="Ajoutez un commentaire sur le traitement de ce signalement..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B00] focus:border-transparent resize-none"
                      rows={6}
                    />
                  </div>

                  {signalement.commentaire_traitement && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2">Commentaire précédent:</h4>
                      <p className="text-blue-700 whitespace-pre-wrap">{signalement.commentaire_traitement}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer - Actions */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => handleUpdateStatus('EN_COURS')}
                  disabled={updating || signalement.statut === 'EN_COURS'}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Clock size={18} />
                  En cours
                </button>
                <button
                  onClick={() => handleUpdateStatus('RESOLU')}
                  disabled={updating || signalement.statut === 'RESOLU'}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={18} />
                  Résolu
                </button>
                <button
                  onClick={() => handleUpdateStatus('REJETE')}
                  disabled={updating || signalement.statut === 'REJETE'}
                  className="px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle size={18} />
                  Rejeter
                </button>
                <button
                  onClick={() => handleUpdateStatus('EN_ATTENTE')}
                  disabled={updating || signalement.statut === 'EN_ATTENTE'}
                  className="px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <AlertTriangle size={18} />
                  En attente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalementDetail;
